import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import TabPanel from "@mui/lab/TabPanel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import HomeIcon from "@mui/icons-material/Home";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReplayIcon from "@mui/icons-material/Replay";

import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { retrieveFinishedOrders } from "./selector";
import { Product } from "../../../lib/types/product";
import { serverApi } from "../../../lib/config";
import { Order, OrderItem } from "../../../lib/types/order";
import { ProductFormat } from "../../../lib/enums/product.enum";

const finishedOrdersRetriever = createSelector(
  retrieveFinishedOrders,
  (finishedOrders) => ({ finishedOrders })
);

function getFirstProduct(order: Order): Product | undefined {
  if (!order?.orderItems?.length || !order?.productData?.length) return undefined;
  const firstItem: OrderItem = order.orderItems[0];
  return order.productData.find((p: Product) => p._id === firstItem.productId);
}

function getProductForItem(order: Order, item: OrderItem): Product | undefined {
  return order.productData?.find((p: Product) => p._id === item.productId);
}

function formatDeliveredDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatProductFormat(format: ProductFormat | undefined): string {
  if (!format) return "";
  const map: Record<string, string> = {
    [ProductFormat.PAPERBACK]: "Paperback",
    [ProductFormat.HARDCOVER]: "Hardcover",
    [ProductFormat.E_BOOK]: "E-Book",
    [ProductFormat.AUDIOBOOK]: "Audiobook",
  };
  return map[format] || format;
}

export default function FinishedOrders() {
  const history = useHistory();
  const { finishedOrders } = useSelector(finishedOrdersRetriever);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const toggleViewDetails = (orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const handleBuyAgain = () => {
    history.push("/products");
  };

  const handleWriteReview = () => {
    // TODO: open review modal or page when implemented
  };

  const handleInvoice = () => {
    // TODO: open invoice PDF or page when integrated
  };

  return (
    <TabPanel value={"3"}>
      <Stack className="finished-orders-stack">
        {finishedOrders.map((order: Order) => {
          const firstProduct = getFirstProduct(order);
          const orderIdShort = order._id.slice(-4);
          const hasOtherProducts = (order.orderItems?.length ?? 0) > 1;
          const isExpanded = expandedOrderId === order._id;
          const deliveredDate = formatDeliveredDate(order.updatedAt ?? order.createdAt);

          if (!firstProduct) return null;

          const imagePath = firstProduct.productImages?.length
            ? `${serverApi}/${firstProduct.productImages[0]}`
            : "/icons/noimage-list.svg";

          const formatLabel = formatProductFormat(firstProduct.productFormat);
          const subtitle = [formatLabel, firstProduct.productAuthor].filter(Boolean).join(" • ");

          return (
            <Box key={order._id} className="finished-order-card">
              {/* Header: DELIVERED + date | Order # | Invoice (same design as process tracking header) */}
              <Box className="finished-order-tracking-header">
                <Box className="finished-order-tracking-header-left">
                  <Box className="finished-order-tracking-icon-wrap">
                    <CheckCircleIcon className="finished-order-truck-icon" />
                  </Box>
                  <Box>
                    <span className="finished-order-status-label">DELIVERED</span>
                    <span className="finished-order-arriving">{deliveredDate}</span>
                  </Box>
                </Box>
                <Box className="finished-order-tracking-header-right">
                  <span className="finished-order-id">Order #BK-{orderIdShort}</span>
                  <span className="finished-order-divider-v">|</span>
                  <button
                    type="button"
                    className="finished-order-invoice-link"
                    onClick={handleInvoice}
                  >
                    Invoice
                  </button>
                </Box>
              </Box>

              <Box className="finished-order-body">
                <img
                  src={imagePath}
                  alt={firstProduct.productName}
                  className="finished-order-product-img"
                />
                <Box className="finished-order-content">
                  <h3 className="finished-order-product-title">{firstProduct.productName}</h3>
                  {subtitle && (
                    <p className="finished-order-product-meta">{subtitle}</p>
                  )}
                  {/* Tracking line: all steps done, green (same structure as process, slightly larger) */}
                  <Box className="finished-order-steps process-order-steps process-order-steps--below-author process-order-steps--small">
                    <Box className="process-order-step process-order-step-done">
                      <span className="process-order-step-icon"><CheckIcon /></span>
                      <span className="process-order-step-label">Placed</span>
                    </Box>
                    <Box className="process-order-step-bar process-order-step-bar-done" />
                    <Box className="process-order-step process-order-step-done">
                      <span className="process-order-step-icon"><CheckIcon /></span>
                      <span className="process-order-step-label">Paid</span>
                    </Box>
                    <Box className="process-order-step-bar process-order-step-bar-done" />
                    <Box className="process-order-step process-order-step-done">
                      <span className="process-order-step-icon"><LocalShippingIcon /></span>
                      <span className="process-order-step-label">On Way</span>
                    </Box>
                    <Box className="process-order-step-bar process-order-step-bar-done" />
                    <Box className="process-order-step process-order-step-done">
                      <span className="process-order-step-icon"><HomeIcon /></span>
                      <span className="process-order-step-label">Delivered</span>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {isExpanded && hasOtherProducts && (
                <Box className="finished-order-details-list">
                  {order.orderItems.map((item: OrderItem) => {
                    const product = getProductForItem(order, item);
                    if (!product) return null;
                    const itemImg = product.productImages?.length
                      ? `${serverApi}/${product.productImages[0]}`
                      : "/icons/noimage-list.svg";
                    return (
                      <Box key={item._id} className="finished-order-detail-row">
                        <img src={itemImg} alt={product.productName} className="finished-order-detail-img" />
                        <Box className="finished-order-detail-info">
                          <span className="finished-order-detail-name">{product.productName}</span>
                          {product.productAuthor && (
                            <p className="finished-order-detail-author">{product.productAuthor}</p>
                          )}
                          <span className="finished-order-detail-qty">
                            {item.itemQuantity} × ${item.itemPrice.toFixed(2)} = $
                            {(item.itemQuantity * item.itemPrice).toFixed(2)}
                          </span>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Box className="finished-order-footer">
                <Box className="finished-order-footer-left">
                  <Button
                    variant="outlined"
                    className="finished-order-buy-again-btn"
                    onClick={handleBuyAgain}
                    startIcon={<ReplayIcon />}
                  >
                    Buy Again
                  </Button>
                  <button
                    type="button"
                    className="finished-order-write-review-link"
                    onClick={handleWriteReview}
                  >
                    Write a Review
                  </button>
                </Box>
                {hasOtherProducts && (
                  <Button
                    variant="outlined"
                    className="finished-order-view-details"
                    onClick={() => toggleViewDetails(order._id)}
                  >
                    {isExpanded ? "Hide details" : "View details"}
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}

        {(!finishedOrders || finishedOrders.length === 0) && (
          <Box display="flex" flexDirection="row" justifyContent="center">
            <img
              src="/icons/noimage-list.svg"
              style={{ width: 300, height: 300 }}
              alt="No orders"
            />
          </Box>
        )}
      </Stack>
    </TabPanel>
  );
}
